import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { TelegramService } from '../../services/telegramService';
import { ReminderService } from '../../services/reminderService';
import PremiumGate from '../PremiumGate';

interface ClientMessagingProps {
  selectedUserId: string; // This is the client's telegram_user_id
  clientName: string;
  telegramUserId: string;
  onUpgrade?: () => void;
}

interface MessageTemplate {
  id: string;
  template_name: string;
  template_content: string;
  template_type: string;
  is_default: boolean;
}

interface Message {
  id: string;
  message: string;
  message_type: string;
  subject?: string;
  is_read: boolean;
  is_sent: boolean;
  scheduled_for?: string;
  created_at: string;
}

const ClientMessaging: React.FC<ClientMessagingProps> = ({
  selectedUserId,
  clientName,
  telegramUserId,
  onUpgrade,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('general');
  const [subject, setSubject] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [recentMessages, setRecentMessages] = useState<Message[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [trainerId, setTrainerId] = useState<string>('');
  const [botStatus, setBotStatus] = useState<'checking' | 'ready' | 'error'>('checking');
  const [isSendingReminder, setIsSendingReminder] = useState(false);

  // Get trainer ID and check bot status on component mount
  useEffect(() => {
    const initialize = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setTrainerId(user.id);
      }

      // Check if Telegram bot is configured
      const botInfo = await TelegramService.getBotInfo();
      if (botInfo) {
        setBotStatus('ready');
        console.log('Telegram bot ready:', botInfo.username);
      } else {
        setBotStatus('error');
        console.log('Telegram bot not configured');
      }
    };
    initialize();
  }, []);

  // Load message templates
  useEffect(() => {
    if (trainerId) {
      loadTemplates();
      loadRecentMessages();
    }
  }, [trainerId]);

           const loadTemplates = async () => {
        try {
          const { data, error } = await supabase
            .from('message_templates')
            .select('*')
            .or(`trainer_id.eq.${trainerId},trainer_id.is.null`)
            .order('template_name');

          if (error) {
            console.error('Template loading error:', error);
            // Try a simpler query if the OR query fails
            const { data: fallbackData, error: fallbackError } = await supabase
              .from('message_templates')
              .select('*')
              .order('template_name');
            
            if (fallbackError) throw fallbackError;
            setTemplates(fallbackData || []);
          } else {
            setTemplates(data || []);
          }
        } catch (error) {
          console.error('Error loading templates:', error);
        }
      };

  const loadRecentMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('telegram_user_id', telegramUserId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setRecentMessages(data || []);
    } catch (error) {
      console.error('Error loading recent messages:', error);
    }
  };

     const handleSendMessage = async () => {
     if (!message.trim()) return;

     setIsSending(true);
     try {
                                console.log('Sending message with data:', {
           trainer_id: trainerId,
           telegram_user_id: telegramUserId,
           client_name: clientName,
           message: message.trim(),
           message_type: messageType
         });

        // First, save to database
                 const { data, error } = await supabase
           .from('messages')
           .insert({
             trainer_id: trainerId,
             telegram_user_id: telegramUserId,
             client_name: clientName,
             message: message.trim(),
             message_type: messageType,
             is_sent: false // Will be updated after Telegram send
           })
           .select();

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        console.log('Message saved to database:', data);

        // Then send via Telegram
        const telegramSent = await TelegramService.sendMessage(
          telegramUserId,
          `💬 <b>Message from your trainer:</b>\n\n${message.trim()}`
        );

        // Update database with send status
        if (telegramSent && data && data[0]) {
          await supabase
            .from('messages')
            .update({ is_sent: true })
            .eq('id', data[0].id);
        }

        console.log('Telegram send result:', telegramSent);

                // Reset form
         setMessage('');
         setMessageType('general');
         setIsModalOpen(false);
       
       // Reload recent messages
       await loadRecentMessages();

               // Show success message
        if (telegramSent) {
          alert('Message sent successfully via Telegram! 📱');
        } else {
          alert('Message saved but failed to send via Telegram. Please check bot configuration.');
        }
     } catch (error) {
       console.error('Error sending message:', error);
       alert('Failed to send message. Please try again.');
     } finally {
       setIsSending(false);
     }
   };

     const handleTemplateSelect = (templateId: string) => {
     const template = templates.find(t => t.id === templateId);
     if (template) {
       setMessage(template.template_content.replace('{{client_name}}', clientName));
       setMessageType(template.template_type);
       setSelectedTemplate(templateId);
     }
   };

   const handleSendReminder = async (reminderType: 'meal_logging' | 'progress_check' | 'goal_review') => {
     setIsSendingReminder(true);
     try {
       let success = false;
       
       switch (reminderType) {
         case 'meal_logging':
           success = await ReminderService.sendMealReminder(telegramUserId, clientName);
           break;
         case 'progress_check':
           success = await ReminderService.sendProgressReminder(telegramUserId, clientName);
           break;
         case 'goal_review':
           success = await ReminderService.sendGoalReminder(telegramUserId, clientName);
           break;
       }

       if (success) {
         alert(`${reminderType.replace('_', ' ')} reminder sent successfully! 📱`);
       } else {
         alert('Failed to send reminder. Please check bot configuration.');
       }
     } catch (error) {
       console.error('Error sending reminder:', error);
       alert('Failed to send reminder. Please try again.');
     } finally {
       setIsSendingReminder(false);
     }
   };

  const getMessageTypeIcon = (type: string) => {
    switch (type) {
      case 'reminder': return '⏰';
      case 'celebration': return '🎉';
      case 'goal': return '🎯';
      case 'progress': return '📊';
      default: return '💬';
    }
  };

  const getMessageTypeColor = (type: string) => {
    switch (type) {
      case 'reminder': return 'bg-yellow-100 text-yellow-800';
      case 'celebration': return 'bg-green-100 text-green-800';
      case 'goal': return 'bg-blue-100 text-blue-800';
      case 'progress': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <PremiumGate
      featureName="Automated Messaging"
      description="Send custom messages, daily stats, and quick reminders to clients via Telegram."
      onUpgrade={onUpgrade}
    >
    <div className="space-y-4">
             {/* Send Message Button */}
               <button
          onClick={() => setIsModalOpen(true)}
          className="bg-neon-blue text-text-dark px-4 py-2 rounded-lg hover:bg-blue-600 hover:text-white transition-colors flex items-center gap-2"
        >
          <span>💬</span>
          Send Message to {clientName}
          {botStatus === 'ready' && <span className="text-xs">📱</span>}
          {botStatus === 'error' && <span className="text-xs">⚠️</span>}
        </button>

             {/* Quick Reminders */}
       <div className="bg-white rounded-lg p-4 shadow-sm">
         <h3 className="text-lg font-semibold mb-3 text-text-dark">Quick Reminders</h3>
         <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
           <button
             onClick={() => handleSendReminder('meal_logging')}
             disabled={isSendingReminder || botStatus !== 'ready'}
             className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors disabled:opacity-50"
           >
             <span>🍽️</span>
             <span className="text-sm font-medium text-text-dark">Meal Reminder</span>
           </button>
           
           <button
             onClick={() => handleSendReminder('progress_check')}
             disabled={isSendingReminder || botStatus !== 'ready'}
             className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
           >
             <span>📊</span>
             <span className="text-sm font-medium text-text-dark">Progress Check</span>
           </button>
           
           <button
             onClick={() => handleSendReminder('goal_review')}
             disabled={isSendingReminder || botStatus !== 'ready'}
             className="flex items-center gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-50"
           >
             <span>🎯</span>
             <span className="text-sm font-medium text-text-dark">Goal Review</span>
           </button>
         </div>
       </div>

       {/* Recent Messages */}
       {recentMessages.length > 0 && (
         <div className="bg-white rounded-lg p-4 shadow-sm">
           <h3 className="text-lg font-semibold mb-3 text-text-dark">Recent Messages</h3>
           <div className="space-y-2">
             {recentMessages.map((msg) => (
               <div key={msg.id} className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                 <span className="text-lg">{getMessageTypeIcon(msg.message_type)}</span>
                 <div className="flex-1">
                   <div className="flex items-center gap-2 mb-1">
                     <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMessageTypeColor(msg.message_type)}`}>
                       {msg.message_type}
                     </span>
                     <span className="text-xs text-gray-500">
                       {new Date(msg.created_at).toLocaleDateString()}
                     </span>
                   </div>
                   <p className="text-sm text-text-dark">{msg.message}</p>
                 </div>
               </div>
             ))}
           </div>
         </div>
       )}

      {/* Message Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-text-dark">
                Send Message to {clientName}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {/* Message Type Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-dark mb-2">
                Message Type
              </label>
              <select
                value={messageType}
                onChange={(e) => setMessageType(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-neon-blue focus:border-transparent"
              >
                <option value="general">💬 General</option>
                <option value="reminder">⏰ Reminder</option>
                <option value="celebration">🎉 Celebration</option>
                <option value="goal">🎯 Goal</option>
                <option value="progress">📊 Progress</option>
              </select>
            </div>

            {/* Template Selection */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-text-dark">
                  Use Template
                </label>
                <button
                  onClick={() => setShowTemplates(!showTemplates)}
                  className="text-sm text-neon-blue hover:underline"
                >
                  {showTemplates ? 'Hide' : 'Show'} Templates
                </button>
              </div>
              
              {showTemplates && (
                <div className="mb-3">
                  <select
                    value={selectedTemplate}
                    onChange={(e) => handleTemplateSelect(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-neon-blue focus:border-transparent"
                  >
                    <option value="">Select a template...</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.template_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            

            {/* Message Content */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-dark mb-2">
                Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message here..."
                rows={4}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-neon-blue focus:border-transparent resize-none"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
                             <button
                 onClick={handleSendMessage}
                 disabled={isSending || !message.trim()}
                 className="flex-1 px-4 py-2 bg-neon-blue text-text-dark rounded-lg hover:bg-blue-600 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
               >
                 {isSending ? 'Sending...' : 'Send Message'}
               </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </PremiumGate>
  );
};

export default ClientMessaging; 